import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { Alert, Button, Portal, Snackbar, SnackbarContent, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LiveText } from '@/components/LiveRegion';
import { resolveTokens } from '@/theme/theme';

export type NotifySeverity = 'success' | 'info' | 'warning' | 'error';

export interface NotifyAction {
  label: string;
  onClick: () => void;
}

export interface NotifyOptions {
  severity?: NotifySeverity;
  action?: NotifyAction;
}

type Notice = {
  id: number;
  message: string;
  severity: NotifySeverity;
  action?: NotifyAction;
};

type NotifyFn = (message: string, options?: NotifyOptions) => void;

const NotifyContext = createContext<NotifyFn | null>(null);
const AUTO_HIDE_MS = 8000;

/** A full-screen dialog (below `sm`, TMMAPP-0089) is open: its action row sits at the bottom. */
function fullScreenDialogOpen(): boolean {
  return document.querySelector('.MuiDialog-root .MuiDialog-paperFullScreen') !== null;
}

export function useNotify(): NotifyFn {
  const notify = useContext(NotifyContext);
  if (!notify) throw new Error('useNotify() must be used inside <NotifyProvider>.');
  return notify;
}

/**
 * One snackbar for the app (TMMAPP-0089). Mounted above the router outlet so a toast survives
 * navigation. FIFO, Dismiss on every toast, 8s unless the toast is hovered or focused.
 * Errors use the error tone on `surfaceRaised` (the outlined-alert pair). Other severities use
 * the existing inverse snackbar surface.
 *
 * Announced (A11Y-007, TMMAPP-0097 §C 28): each notice renders through a `Portal` into
 * `document.body`, mounted when it becomes current, so it lands after any modal that is already
 * open and outside the `aria-hidden` that modal put on the app root. The `role="status"` is the
 * message alone (not Dismiss, not the icon); it mounts empty and the message is written a moment
 * after mount (`LiveText`), so it is announced as a change. A notice leaves without the snackbar's
 * exit transition: its portal unmounts with it.
 *
 * A snackbar never covers a dialog's actions (TMMAPP-0113 4c, the 0110 review): while a full-screen
 * dialog is open, the notice sits at the top instead of over the dialog's bottom action row. Dialogs
 * portal into `document.body`, so its children are watched while a notice shows.
 */
export function NotifyProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const [queue, setQueue] = useState<Notice[]>([]);
  const [paused, setPaused] = useState(false);
  const nextId = useRef(0);
  const statusId = useId();
  const current = queue[0] ?? null;
  const [atTop, setAtTop] = useState(false);

  const notify = useCallback<NotifyFn>((message, options) => {
    nextId.current += 1;
    const notice: Notice = {
      id: nextId.current,
      message,
      severity: options?.severity ?? 'success',
      ...(options?.action !== undefined ? { action: options.action } : {}),
    };
    setQueue((prev) => [...prev, notice]);
  }, []);

  const dismiss = useCallback(() => {
    setQueue((prev) => prev.slice(1));
    setPaused(false);
  }, []);

  useEffect(() => {
    if (current === null) return undefined;
    const update = () => setAtTop(fullScreenDialogOpen());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [current]);

  useEffect(() => {
    if (current === null || paused) return;
    const timer = window.setTimeout(dismiss, AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [current, paused, dismiss]);

  const actions = current ? (
    <>
      {current.action !== undefined && (
        <Button color="inherit" size="small" onClick={current.action.onClick}>
          {current.action.label}
        </Button>
      )}
      <Button color="inherit" size="small" onClick={dismiss}>
        {t('notifier.dismiss')}
      </Button>
    </>
  ) : null;

  function pauseOnFocus(event: FocusEvent<HTMLElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(true);
  }

  function resumeOnBlur(event: FocusEvent<HTMLElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
  }

  // The polite region holds only the message: it mounts empty and the text arrives after mount.
  const message =
    current === null ? null : (
      <span role="status" aria-live="polite" id={statusId}>
        <LiveText text={current.message} />
      </span>
    );

  const surface =
    current === null ? null : current.severity === 'error' ? (
      <Alert
        severity="error"
        variant="outlined"
        // The live region is the message alone (below), never the Dismiss button or the icon.
        role="presentation"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={pauseOnFocus}
        onBlur={resumeOnBlur}
        action={actions}
        sx={{
          bgcolor: tokens.surfaceRaised,
          color: tokens.errorText,
          borderColor: tokens.errorFg,
          alignItems: 'center',
          maxWidth: 420,
          borderRadius: `${tokens.radius.control}px`,
          '& .MuiAlert-icon': { color: tokens.errorFg },
          '& .MuiAlert-action .MuiButton-root': { color: tokens.errorText },
        }}
      >
        {message}
      </Alert>
    ) : (
      <SnackbarContent
        role="presentation"
        message={message}
        action={actions}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={pauseOnFocus}
        onBlur={resumeOnBlur}
      />
    );

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      {current !== null && (
        <Portal key={current.id}>
          {/* Bottom-left 24 / 24 at every width (TMMAPP-0099, P:2623; replaces 0089's phone
              centring), never wider than the viewport allows. */}
          <Snackbar
            open
            anchorOrigin={{ vertical: atTop ? 'top' : 'bottom', horizontal: 'left' }}
            sx={{
              left: 24,
              right: 'auto',
              maxWidth: 'calc(100% - 48px)',
              ...(atTop ? { top: 24, bottom: 'auto' } : { bottom: 24 }),
              [theme.breakpoints.up('sm')]: {
                left: 24,
                right: 'auto',
                ...(atTop ? { top: 24, bottom: 'auto' } : { bottom: 24 }),
              },
            }}
          >
            {surface ?? <span />}
          </Snackbar>
        </Portal>
      )}
    </NotifyContext.Provider>
  );
}
